import { useState, useRef } from "react";
import { messengerService } from "../../../functions/messenger";
import "./style.css";

export default function ConversationItem({
  conversation,
  currentUser,
  isSelected,
  onSelect,
  isOnline,
  onConversationDeleted,
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const tapTimeoutRef = useRef(null);
  const lastTapRef = useRef(0);
  // For private chats, if the other user is deleted, do not render this item
  if (
    conversation.type !== 'group' &&
    !conversation.participants.find(p => p._id !== currentUser.id)
  ) {
    return null;
  }

  // Do not render active group conversations in the chats list — those live
  // in the Groups tab. Group conversations the user has left, however, stay
  // here in read-only form so they can review history or delete them.
  if (conversation.type === 'group' && !conversation.userLeft) {
    return null;
  }

  const getConversationName = () => {
    if (conversation.type === 'group') {
      return conversation.name || 'Group Chat';
    }
    
    const otherUser = conversation.participants.find(p => p._id !== currentUser.id);
    return otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : '';
  };

  const getConversationPicture = () => {
    if (conversation.type === 'group') {
      return conversation.groupPicture || conversation.picture || '/images/default_group.svg';
    }
    
    const otherUser = conversation.participants.find(p => p._id !== currentUser.id);
    return otherUser?.picture || '/images/default_profile.png';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getLastMessagePreview = () => {
    if (!conversation.lastMessage) return 'No messages yet';
    
    const { lastMessage } = conversation;
    const isCurrentUser = lastMessage.sender?._id === currentUser.id;
    const senderName = isCurrentUser ? 'You' : (lastMessage.sender?.first_name || 'Someone');

    // Mirror the chat bubble: when the message has been deleted for everyone,
    // show the same placeholder in the conversation list preview.
    if (lastMessage.deleted?.isDeleted) {
      return 'This message was deleted';
    }

    switch (lastMessage.type) {
      case 'text':
        return `${senderName}: ${lastMessage.content.text}`;
      case 'image':
        return `${senderName} sent a photo`;
      case 'video':
        return `${senderName} sent a video`;
      case 'audio':
        const audioDuration = lastMessage.content.media?.[0]?.duration;
        const durationText = audioDuration ? ` (${formatDuration(audioDuration)})` : '';
        return `${senderName} sent a voice message${durationText}`;
      case 'file':
        return `${senderName} sent a file`;
      case 'call':
        const callType = lastMessage.content.call.type;
        const callStatus = lastMessage.content.call.status;
        return `${senderName} ${callStatus === 'missed' ? 'missed' : 'made'} a ${callType} call`;
      case 'system': {
        const action = lastMessage.content?.system?.action;
        const data = lastMessage.content?.system?.data || {};
        if (lastMessage.content?.text) return lastMessage.content.text;
        if (action === 'user_added') return `${data.actorName || 'Admin'} added ${data.userName || 'a member'}`;
        if (action === 'user_removed') return `${data.actorName || 'Admin'} removed ${data.userName || 'a member'}`;
        if (action === 'user_left') return `${data.userName || 'A member'} left the group`;
        return 'Group updated';
      }
      default:
        return 'New message';
    }
  };

  const hasUnreadMessages = () => {
    // Use the unreadCount from the conversation data
    return conversation.unreadCount > 0;
  };

  const getUnreadCount = () => {
    // Return the actual unread count, but cap it at 99+ for display
    const count = conversation.unreadCount || 0;
    return count > 99 ? '99+' : count.toString();
  };

  const getUnreadCountClass = () => {
    const count = conversation.unreadCount || 0;
    if (count === 0) return '';
    if (count <= 3) return 'count-low';
    if (count <= 10) return 'count-medium';
    if (count <= 50) return 'count-high';
    return 'count-urgent';
  };

  const getConversationItemClasses = () => {
    const baseClasses = `conversation_item ${isSelected ? 'selected' : ''} ${conversation.pinned ? 'pinned' : ''} ${conversation.muted ? 'muted' : ''}`;
    return hasUnreadMessages() ? `${baseClasses} has-unread` : baseClasses;
  };

  const handleTap = () => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;

    if (lastTapRef.current && (now - lastTapRef.current) < DOUBLE_TAP_DELAY) {
      // Double tap detected
      clearTimeout(tapTimeoutRef.current);
      setShowDeleteModal(true);
    } else {
      // Single tap - select conversation after delay
      tapTimeoutRef.current = setTimeout(() => {
        onSelect();
      }, DOUBLE_TAP_DELAY);
    }
    
    lastTapRef.current = now;
  };

  const handleDeleteConversation = async () => {
    try {
      setIsDeleting(true);
      await messengerService.deleteConversation(conversation._id, currentUser.token);
      
      // Show success notification
      const notification = document.createElement('div');
      notification.textContent = 'Conversation deleted successfully';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #51cf66;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
      
      // Notify parent component to remove conversation from list
      if (onConversationDeleted) {
        onConversationDeleted(conversation._id);
      }
      
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      
      // Show error notification
      const notification = document.createElement('div');
      notification.textContent = 'Failed to delete conversation. Please try again.';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff6b6b;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        className={getConversationItemClasses()}
        onClick={handleTap}
        data-conversation-id={conversation._id}
      >
        <div className="conversation_picture_container">
          <img
            src={getConversationPicture()}
            alt={getConversationName()}
            className="conversation_picture"
          />
          {isOnline && conversation.type === 'private' && (
            <div className="online_indicator"></div>
          )}
        </div>
        
        <div className="conversation_details">
          <div className="conversation_header">
            <h4 className="conversation_name">{getConversationName()}</h4>
          </div>
          
          <div className="conversation_preview">
            <p className="last_message">{getLastMessagePreview()}</p>
            <div className="conversation_indicators">
              {conversation.pinned && (
                <div className="pin_indicator" title="Pinned conversation">
                  📌
                </div>
              )}
              {conversation.muted && (
                <div className="mute_indicator" title="Muted conversation">
                  🔇
                </div>
              )}
              {hasUnreadMessages() && (
                <div className="unread_indicator">
                  <span className={`unread_count ${getUnreadCountClass()}`}>
                    {getUnreadCount()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete_modal_overlay">
          <div className="delete_modal">
            <div className="delete_modal_content">
              <div className="delete_modal_icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3>Delete Conversation</h3>
              <p>Are you sure you want to delete this conversation? This action cannot be undone.</p>
              <div className="delete_modal_actions">
                <button 
                  className="delete_modal_cancel"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button 
                  className="delete_modal_confirm"
                  onClick={handleDeleteConversation}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}